// Iter48 — Standalone Process FMEA creation modal.
// Multi-step table editor with per-step S/O/D + auto-calculated RPN.
// Supports both standalone (incident_id=null) and incident-linked creation.
import React, { useMemo, useState } from "react";
import { X, Plus, Trash2, Check, Link2, FileText, Search, Sparkles } from "lucide-react";
import { useStore } from "../../store";
import { useIncidents } from "../../api/sitroom";

const STEP_BLANK = {
  step_number: "",
  step_name: "",
  function: "",
  failure_mode: "",
  effects: "",
  severity: 5,
  causes: "",
  occurrence: 5,
  controls: "",
  detection: 5,
};

const rpnOf = (s) => (Number(s.severity) || 0) * (Number(s.occurrence) || 0) * (Number(s.detection) || 0);
const rpnColor = (rpn) => (rpn >= 200 ? "#E24B4A" : rpn >= 100 ? "#f3b14a" : rpn > 0 ? "#4ed084" : "#9198b0");

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "#f0f2f8",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const inputBase = {
  background: "#08090d",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "#f0f2f8",
  ...mono(11),
  padding: "5px 8px",
  borderRadius: 4,
  outline: "none",
  width: "100%",
};

const Label = ({ children }) => (
  <div className="mb-1 uppercase" style={mono(8, "#9198b0", 700, "0.8px")}>{children}</div>
);

export const NewProcessFmeaModal = ({ open, onClose, onCreated, initialIncidentId = null }) => {
  const addToast = useStore((s) => s.addToast);
  const [title, setTitle] = useState("");
  const [processName, setProcessName] = useState("");
  const [scope, setScope] = useState("");
  const [team, setTeam] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [linkKind, setLinkKind] = useState(initialIncidentId ? "linked" : "standalone");
  const [incidentId, setIncidentId] = useState(initialIncidentId || "");
  const [steps, setSteps] = useState([{ ...STEP_BLANK, step_number: "10" }]);
  const [busy, setBusy] = useState(false);
  const [incidentSearch, setIncidentSearch] = useState("");

  const { data: incidents = [] } = useIncidents();

  const filteredIncidents = useMemo(() => {
    if (!incidentSearch) return incidents.slice(0, 20);
    const q = incidentSearch.toLowerCase();
    return incidents.filter((i) =>
      (i.id || "").toLowerCase().includes(q) || (i.title || "").toLowerCase().includes(q)
    ).slice(0, 20);
  }, [incidents, incidentSearch]);

  const summary = useMemo(() => {
    const rpns = steps.map(rpnOf);
    return {
      highest: rpns.length ? Math.max(...rpns) : 0,
      above: rpns.filter((r) => r > 100).length,
      avg: rpns.length ? Math.round((rpns.reduce((a, b) => a + b, 0) / rpns.length) * 10) / 10 : 0,
    };
  }, [steps]);

  const addStep = () => {
    const nextNum = String(((Number(steps.at(-1)?.step_number) || 0) + 10) || (steps.length + 1) * 10);
    setSteps([...steps, { ...STEP_BLANK, step_number: nextNum }]);
  };
  const removeStep = (idx) => setSteps((s) => s.filter((_, i) => i !== idx));
  const updateStep = (idx, field, value) =>
    setSteps((s) => s.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));

  // Iter51 — AI RPN scorer per row.
  const [aiBusy, setAiBusy] = useState({}); // {idx: true}
  const aiScoreRow = async (idx) => {
    const row = steps[idx];
    if (!row.failure_mode?.trim() && !row.effects?.trim() && !row.causes?.trim()) {
      addToast({ type: "info", title: "Add at least failure mode, effects or causes before AI scoring" });
      return;
    }
    setAiBusy((b) => ({ ...b, [idx]: true }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fmea/score-rpn`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sitroom_token") || ""}`,
        },
        body: JSON.stringify({
          failure_mode: row.failure_mode || "",
          failure_effect: row.effects || "",
          potential_cause: row.causes || "",
          current_controls: row.controls || "",
          incident_severity: linkKind === "linked" ? "linked" : "n/a",
          production_impact: row.effects || row.failure_mode || "",
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSteps((s) => s.map((r, i) => i === idx
        ? { ...r, severity: data.sev, occurrence: data.occ, detection: data.det }
        : r,
      ));
      addToast({
        type: "success",
        title: `AI RPN scored · S${data.sev} · O${data.occ} · D${data.det} = ${data.sev * data.occ * data.det}`,
        body: `S: ${data.sev_reason}\nO: ${data.occ_reason}\nD: ${data.det_reason}`,
      });
    } catch (e) {
      addToast({ type: "error", title: "AI RPN scoring failed", body: e.message?.slice(0, 200) });
    } finally {
      setAiBusy((b) => { const c = { ...b }; delete c[idx]; return c; });
    }
  };

  const submit = async () => {
    if (!title.trim() || !processName.trim() || steps.length === 0) {
      addToast({ type: "info", title: "Title, process name and at least one step are required" });
      return;
    }
    setBusy(true);
    try {
      const body = {
        type: "process",
        title: title.trim(),
        process_name: processName.trim(),
        scope: scope.trim() || null,
        team: team.trim() || null,
        date,
        steps,
        incident_id: linkKind === "linked" ? incidentId : null,
        sourceIncident: linkKind === "linked" ? incidentId : null,
        // Legacy single-line fallback so the existing FMEA Row component renders nicely
        failureMode: steps[0]?.failure_mode || title.trim(),
        effect: steps[0]?.effects || "—",
        cause: steps[0]?.causes || "—",
        severity: steps[0]?.severity,
        occurrence: steps[0]?.occurrence,
        detection: steps[0]?.detection,
        businessFunction: steps[0]?.function || null,
        processStep: steps[0]?.step_name || null,
      };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fmeas`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sitroom_token") || ""}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const created = await res.json();
      addToast({ type: "success", title: "Process FMEA created", body: `${created.id} · ${steps.length} step(s) · highest RPN ${summary.highest}` });
      onCreated?.(created);
      onClose();
    } catch (e) {
      addToast({ type: "error", title: "Create failed", body: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[500] flex items-start sm:items-center justify-center p-2 sm:p-6"
      style={{ background: "rgba(8,9,13,0.85)", backdropFilter: "blur(6px)" }}
      data-testid="new-process-fmea-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-6xl rounded-lg flex flex-col"
        style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)", maxHeight: "92vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="flex items-center gap-2">
            <FileText size={16} color="#c89adb" />
            <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "#f0f2f8" }}>
              New Process FMEA
            </span>
          </div>
          <button type="button" onClick={onClose} data-testid="fmea-modal-close"
            style={{ background: "transparent", border: "none", color: "#9198b0", cursor: "pointer" }}><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 flex-1">
          {/* Link kind */}
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            {[
              { k: "standalone", label: "Standalone (no incident)", icon: FileText },
              { k: "linked", label: "Link to existing incident", icon: Link2 },
            ].map((opt) => (
              <button key={opt.k} type="button" onClick={() => setLinkKind(opt.k)}
                className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
                style={{
                  background: linkKind === opt.k ? "rgba(142,68,173,0.18)" : "transparent",
                  border: `1px solid ${linkKind === opt.k ? "rgba(142,68,173,0.55)" : "rgba(255,255,255,0.10)"}`,
                  color: linkKind === opt.k ? "#c89adb" : "#9198b0",
                  ...mono(10, undefined, 700, "0.6px"),
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
                data-testid={`fmea-link-kind-${opt.k}`}>
                <opt.icon size={12} /> {opt.label}
              </button>
            ))}
          </div>

          {linkKind === "linked" && (
            <div className="mb-3 p-3 rounded" style={{ background: "rgba(24,95,165,0.06)", border: "1px solid rgba(24,95,165,0.30)" }}>
              <Label>Linked incident</Label>
              <div className="relative">
                <Search size={11} style={{ position: "absolute", left: 8, top: 9, color: "#6b7388" }} />
                <input value={incidentSearch} onChange={(e) => setIncidentSearch(e.target.value)}
                  placeholder="Search incident by id or title…"
                  style={{ ...inputBase, paddingLeft: 26 }} data-testid="fmea-incident-search" />
              </div>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {filteredIncidents.map((i) => (
                  <button key={i.id} type="button" onClick={() => setIncidentId(i.id)}
                    className="w-full text-left px-2 py-1 rounded"
                    style={{
                      background: incidentId === i.id ? "rgba(24,95,165,0.22)" : "transparent",
                      border: `1px solid ${incidentId === i.id ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.06)"}`,
                      color: "#cfd1d8", ...mono(10, undefined, 600, "0.3px"), cursor: "pointer",
                    }}
                    data-testid={`fmea-incident-option-${i.id}`}>
                    <b style={{ color: "#9ec3ec" }}>{i.id}</b> — {i.title || "(no title)"}
                  </button>
                ))}
                {filteredIncidents.length === 0 && (
                  <div style={mono(10, "#9198b0")}>No incidents match.</div>
                )}
              </div>
              {incidentId && <div className="mt-2" style={mono(10, "#4ed084")}>Selected: {incidentId}</div>}
            </div>
          )}

          {/* Header fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <Label>Title *</Label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputBase} data-testid="fmea-modal-title" id="fmea-title" />
            </div>
            <div>
              <Label>Process name *</Label>
              <input value={processName} onChange={(e) => setProcessName(e.target.value)} style={inputBase} data-testid="fmea-modal-process-name" id="fmea-process-name" />
            </div>
            <div className="md:col-span-2">
              <Label>Scope</Label>
              <input value={scope} onChange={(e) => setScope(e.target.value)} placeholder="What does this FMEA cover?" style={inputBase} data-testid="fmea-scope" />
            </div>
            <div>
              <Label>Team</Label>
              <input value={team} onChange={(e) => setTeam(e.target.value)} placeholder="Names / department" style={inputBase} data-testid="fmea-team" />
            </div>
            <div>
              <Label>Date</Label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputBase} data-testid="fmea-date" />
            </div>
          </div>

          {/* Steps */}
          <div className="mb-2 flex items-center justify-between">
            <div style={mono(10, "#9198b0", 700, "0.8px")} className="uppercase">Process steps · {steps.length}</div>
            <button type="button" onClick={addStep}
              className="px-2 py-1 rounded inline-flex items-center gap-1"
              style={{ background: "rgba(142,68,173,0.18)", color: "#c89adb", border: "1px solid rgba(142,68,173,0.5)", ...mono(10, undefined, 700, "0.4px"), cursor: "pointer" }}
              data-testid="fmea-add-step-btn">
              <Plus size={11} /> Add process step
            </button>
          </div>

          <div className="overflow-x-auto rounded" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#08090d" }}>
                  {["#", "Step name", "Function", "Failure mode", "Effects", "S", "Causes", "O", "Controls", "D", "RPN", ""].map((h) => (
                    <th key={h} className="px-2 py-1.5 text-left"
                      style={{ ...mono(8, "#9198b0", 700, "0.8px"), textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {steps.map((row, idx) => {
                  const rpn = rpnOf(row);
                  return (
                    <tr key={idx} data-testid={`fmea-step-row-${idx}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <td className="px-1 py-1"><input value={row.step_number} onChange={(e) => updateStep(idx, "step_number", e.target.value)} style={{ ...inputBase, width: 50 }} data-testid={`fmea-step-num-${idx}`} /></td>
                      <td className="px-1 py-1"><input value={row.step_name} onChange={(e) => updateStep(idx, "step_name", e.target.value)} style={inputBase} data-testid={`fmea-step-name-${idx}`} /></td>
                      <td className="px-1 py-1"><input value={row.function} onChange={(e) => updateStep(idx, "function", e.target.value)} style={inputBase} /></td>
                      <td className="px-1 py-1"><input value={row.failure_mode} onChange={(e) => updateStep(idx, "failure_mode", e.target.value)} style={inputBase} data-testid={`fmea-step-fm-${idx}`} /></td>
                      <td className="px-1 py-1"><input value={row.effects} onChange={(e) => updateStep(idx, "effects", e.target.value)} style={inputBase} /></td>
                      <td className="px-1 py-1"><input type="number" min={1} max={10} value={row.severity} onChange={(e) => updateStep(idx, "severity", e.target.value)} style={{ ...inputBase, width: 56 }} data-testid={`fmea-step-s-${idx}`} /></td>
                      <td className="px-1 py-1"><input value={row.causes} onChange={(e) => updateStep(idx, "causes", e.target.value)} style={inputBase} /></td>
                      <td className="px-1 py-1"><input type="number" min={1} max={10} value={row.occurrence} onChange={(e) => updateStep(idx, "occurrence", e.target.value)} style={{ ...inputBase, width: 56 }} data-testid={`fmea-step-o-${idx}`} /></td>
                      <td className="px-1 py-1"><input value={row.controls} onChange={(e) => updateStep(idx, "controls", e.target.value)} style={inputBase} /></td>
                      <td className="px-1 py-1"><input type="number" min={1} max={10} value={row.detection} onChange={(e) => updateStep(idx, "detection", e.target.value)} style={{ ...inputBase, width: 56 }} data-testid={`fmea-step-d-${idx}`} /></td>
                      <td className="px-1 py-1 text-center">
                        <span style={{ ...mono(13, rpnColor(rpn), 700, "0.4px") }} data-testid={`fmea-step-rpn-${idx}`}>{rpn}</span>
                      </td>
                      <td className="px-1 py-1">
                        <div className="flex items-center gap-1">
                          <button type="button"
                            onClick={() => aiScoreRow(idx)}
                            disabled={Boolean(aiBusy[idx])}
                            style={{
                              background: aiBusy[idx] ? "rgba(142,68,173,0.30)" : "rgba(142,68,173,0.16)",
                              border: "1px solid rgba(142,68,173,0.55)",
                              color: "#c89adb",
                              borderRadius: 3,
                              padding: "3px 6px",
                              cursor: aiBusy[idx] ? "wait" : "pointer",
                              ...mono(9, undefined, 700, "0.4px"),
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 3,
                              textTransform: "uppercase",
                            }}
                            data-testid={`fmea-step-ai-${idx}`}
                            title="Score S/O/D with AI based on this step's context">
                            <Sparkles size={10} /> {aiBusy[idx] ? "…" : "AI"}
                          </button>
                          {steps.length > 1 && (
                            <button type="button" onClick={() => removeStep(idx)}
                              style={{ background: "transparent", border: "1px solid rgba(226,75,74,0.30)", color: "#ff9a99", borderRadius: 3, padding: "3px 6px", cursor: "pointer" }}
                              data-testid={`fmea-step-remove-${idx}`}><Trash2 size={10} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary footer */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="fmea-summary">
            <div className="px-3 py-2 rounded" style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={mono(8, "#9198b0", 700, "0.8px")} className="uppercase">Highest RPN</div>
              <div style={{ ...mono(20, rpnColor(summary.highest), 700) }} data-testid="fmea-summary-highest">{summary.highest}</div>
            </div>
            <div className="px-3 py-2 rounded" style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={mono(8, "#9198b0", 700, "0.8px")} className="uppercase">Steps above threshold (&gt; 100)</div>
              <div style={{ ...mono(20, summary.above > 0 ? "#f3b14a" : "#4ed084", 700) }} data-testid="fmea-summary-above">{summary.above}</div>
            </div>
            <div className="px-3 py-2 rounded" style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={mono(8, "#9198b0", 700, "0.8px")} className="uppercase">Average RPN</div>
              <div style={{ ...mono(20, "#9ec3ec", 700) }} data-testid="fmea-summary-avg">{summary.avg}</div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex justify-end gap-2 px-5 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button type="button" onClick={onClose} disabled={busy}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", ...mono(11, undefined, 600, "0.4px"), cursor: "pointer" }}
            data-testid="fmea-modal-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={busy}
            className="px-4 py-1.5 rounded inline-flex items-center gap-1"
            style={{ background: "#8E44AD", color: "#fff", ...mono(11, undefined, 700, "0.4px"), border: "none", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}
            data-testid="fmea-modal-submit"><Check size={11} /> Create FMEA</button>
        </div>
      </div>
    </div>
  );
};
